/* eslint-disable react/no-multi-comp */
import React from 'react';

import { FormattedMessage } from 'react-intl';

import { programTypeName } from '@modules/shared';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import {
  Button,
  DateView,
  PersonAvatar,
  S,
} from '@shared/components';


import PendingItemAdvancementName from '../PendingItemAdvancementName';
import PendingItemType from '../PendingItemType';
import styles from './PendingItemsTable.less';
import type {
  PendingItem,
  TableClickActions,
} from '@modules/advancement/pendingItems/types';
import type { HistoryItemType } from '@appTypes';


const rows = ({
  onGoToYouthProfile,
  onGoToAdultProfile,
  showRemoveCompletion = false,
  onRemoveCompletion,
  showGoToPODetails = false,
  onGoToPODetails,
}: {
  onGoToYouthProfile: TableClickActions['onGoToYouthProfile'];
  onGoToAdultProfile: TableClickActions['onGoToAdultProfile'];
  showRemoveCompletion?: boolean;
  onRemoveCompletion?: (data: PendingItem) => void;
  showGoToPODetails?: boolean;
  onGoToPODetails?: (poid: string) => void;
}) => (item: PendingItem) => {
  const {
    userId,
    isAdult,
    activityTypeId,
    firstName,
    lastName,
    nickName,
  } = item;
  const personPreferredName = getPreferredName({
    firstName,
    lastName,
    nickName,
  });
  let onClickName = undefined;
  if (isAdult) {
    onClickName = onGoToAdultProfile(userId, activityTypeId);
  } else {
    onClickName = onGoToYouthProfile(userId, activityTypeId);
  }

  const items = [
    {
      rowId: 'name',
      content: (
        <>
          <PersonAvatar
            isAdult={item.isAdult}
            src={item.profilePictureUrl}
          />
          <S
            size="4"
            bold
            colored
            title={personPreferredName}
            onClick={onClickName}
          >
            {personPreferredName}
          </S>
        </>
      ),
    },
    {
      rowId: 'memberId',
      label: (
        <S size="4" bold>
          <FormattedMessage id="advancement.PendingItems.column.memberId" />
        </S>
      ),
      content: <S size="4">{item.memberId}</S>,
    },
    {
      rowId: 'advancement',
      label: (
        <S size="4" bold>
          <PendingItemType item={item} />
        </S>
      ),
      content: (
        <PendingItemAdvancementName
          type={item.type as HistoryItemType}
          item={item}
          showIcon
        />
      ),
    },
    {
      rowId: 'completionDate',
      label: (
        <S size="4" bold>
          <FormattedMessage id="advancement.PendingItems.column.itemCompletionDate" />
        </S>
      ),
      content: (<DateView value={item.date} />),
    },
  ];

  if (showRemoveCompletion && typeof onRemoveCompletion === 'function') {
    items.push({
      rowId: 'remove',
      content: (
        <>
          {item?.program === programTypeName.CUB_SCOUT && (
            <Button
              ghost
              noBorder
              color="info"
              size="small"
              uppercase={false}
              block
              onClick={() => onRemoveCompletion(item)}
            >
              <FormattedMessage id="shared.remove" />
            </Button>
          )}
        </>
      ),
    });
  }

  if (showGoToPODetails && typeof onGoToPODetails === 'function') {
    items.push({
      rowId: 'poid',
      label: (
        <S size="4" bold>
          <FormattedMessage id="advancement.PendingItems.column.poid" />
        </S>
      ),
      content: (
        <S
          size="4"
          bold
          inline
          colored
          title={item.poid}
          className={styles.poid}
          onClick={() => onGoToPODetails(item.poid)}
        >
          {item.poid}
        </S>
      )
    });
  }

  return items;
};

export default rows;
